import React, { useMemo, useState } from 'react';
import _ from 'lodash';

import {
	CustomLPTReport,
	CustomMPIReport,
	NewPCNLItem,
	PCNLAnomalyItem,
	PCNLItemModel,
	PDFImageProps,
	UTMInspection,
} from '../../../../utils/models';
import { getUTMStructureInspectionOptions } from '../../../../utils/data';
import { matchPhrases } from '../../../../utils/utilities';

import AccordionItem from '../../custom-visual-inspection/accordion-item';
import AnomalyLocations from '../../../forms/custom-fvi/anomalyLocations';
import ConcreteHammerTemplate from '../concrete-hammer-template/report-form';
import Conditional from '../../../common/conditional';
import CustomGroupItem from '../../custom-general/custom-group-item';
import CustomUTMInspectionOverviewForm from '../../../forms/custom-utm/customUTMInspectionOverviewForm';
import CustomNewItem from '../../custom-general/custom-new-item';
import CustomUTMInspectionFormNewItem from '../../../forms/custom-utm/customUTMInspectionFormNewItem';
import CustomUTMInspectionItem from '../../custom-utm/custom-utm-inspection-item';
import EditPCNLItemForm from '../../../forms/custom-pcnl/editPCNLItemForm';
import LPTReportForm from '../../../forms/custom-lpt/lptReportForm';
import MPIReportForm from '../../../forms/custom-mpi/mpiReportForm';
import PCNLRiskAssessment from '../pcnl-risk-assessment';
import PCNLAnomaly from '../pcnl-anomaly';
import VIPhotoRecordForm from '../../../forms/visual-inspection/viPhotoRecordForm';

import toastService from '../../../../services/toastService';

interface Props {
	pcnlItemOptions: string[],
	pcnlItem: PCNLItemModel;
	nextAnomalyID: number;
	isOpenOnDefault: boolean;
	onToggleAnomaly: (pcnlItemName: string, anomaly: PCNLAnomalyItem) => void;
	onDeletePCNLItem: () => void;
	onUpdatePCNLItem: (updatedPCNLItem: PCNLItemModel) => void;
	onUpdatePCNLItemWithNewName: (oldPCNLItemName: string, updatedPCNLItem: PCNLItemModel) => void;
}

const PCNLItemForm: React.FC<Props> = ({
	pcnlItemOptions,
	onToggleAnomaly,
	isOpenOnDefault,
	nextAnomalyID,
	pcnlItem,
	onDeletePCNLItem,
	onUpdatePCNLItem,
	onUpdatePCNLItemWithNewName,
}) => {
	const [editPCNLItem, setEditPCNLItem] = useState(false);

	const handlePCNLUpdate= (updatedPCNLItem: NewPCNLItem) => {
		onUpdatePCNLItemWithNewName(pcnlItem.pcnlItemName, { ...pcnlItem, ...updatedPCNLItem });
		setEditPCNLItem(false);
	};

	const handleAddInspectionItem = (newItem: UTMInspection) => {
		const inspectionItemMatch = pcnlItem.utmData.items.find((item) => matchPhrases(item.sectionTitle, newItem.sectionTitle));
		if (inspectionItemMatch) return toastService.error(`Section ${inspectionItemMatch.sectionTitle} already exists!`);

		const newUTMData = {
			overview: { ...pcnlItem.utmData.overview },
			items: [...pcnlItem.utmData.items, newItem],
		};

		const newPCNLItem: PCNLItemModel = {
			..._.omit(pcnlItem, ['utmData']),
			utmData: newUTMData,
		};

		onUpdatePCNLItem(newPCNLItem);
	};

	const handleEditInspectionItem = (originalItem: UTMInspection, updatedItem: UTMInspection) => {
		const newUTMData = {
			overview: { ...pcnlItem.utmData.overview },
			items: pcnlItem.utmData.items.map((item) => item.sectionTitle === originalItem.sectionTitle ? updatedItem : item),
		};

		const newPCNLItem: PCNLItemModel = {
			..._.omit(pcnlItem, ['utmData']),
			utmData: newUTMData,
		};

		onUpdatePCNLItem(newPCNLItem);
	};

	const handleDeleteInspectionItem = (itemToDelete: UTMInspection) => {
		const newUTMData = {
			overview: { ...pcnlItem.utmData.overview },
			items: pcnlItem.utmData.items.filter((item) => item.sectionTitle !== itemToDelete.sectionTitle),
		};

		const newPCNLItem: PCNLItemModel = {
			..._.omit(pcnlItem, ['utmData']),
			utmData: newUTMData,
		};

		onUpdatePCNLItem(newPCNLItem);
	};

	const mappedPhotoNumbers = useMemo(() => {
		return pcnlItem.generalPhotos.map((photo) => ({
			label: photo.caption,
			value: photo.refNo,
		}));
	}, [pcnlItem.generalPhotos]);

	return (
		<>
			<Conditional isVisible={editPCNLItem}>
				<div className="bg-white round border p-3">
					<EditPCNLItemForm
						pcnlItemOptions={pcnlItemOptions}
						initialPCNLItem={_.pick(pcnlItem, ['pcnlItemName', 'hasUTMData', 'hasMPTData', 'hasLPTData', 'hasConcreteData'])}
						onEdit={handlePCNLUpdate}
						onCancelEdit={() => setEditPCNLItem(false)}
					/>
				</div>
			</Conditional>

			<Conditional isVisible={!editPCNLItem}>
				<CustomGroupItem
					isOpenOnDefault={isOpenOnDefault}
					onDelete={onDeletePCNLItem}
					title={pcnlItem.pcnlItemName}
					onEdit={() => setEditPCNLItem(true)}
				>
					<Conditional isVisible={!matchPhrases(pcnlItem.pcnlItemName, 'risk assessment')}>
						<AccordionItem isActive title="Anomaly Items">
							<PCNLAnomaly
								onMoveAnomaly={(anomaly) => onToggleAnomaly(pcnlItem.pcnlItemName, anomaly)}
								nextAnomalyID={nextAnomalyID}
								anomalyItems={pcnlItem.anomalyItems as any}
								onDataChange={(items: any[]) => {
									const newPCNLItems: PCNLItemModel = { ..._.omit(pcnlItem, ['anomalyItems']), anomalyItems: items };
									onUpdatePCNLItem(newPCNLItems);
								}}
							/>
						</AccordionItem>
					</Conditional>

					<Conditional
						isVisible={matchPhrases(pcnlItem.pcnlItemName, 'risk assessment')}
					>
						<AccordionItem isActive title="Assessment">
							<PCNLRiskAssessment
								riskAssessmentItems={pcnlItem.riskAssessment}
								onDataChange={(items) => {
									const newPCNLItems: PCNLItemModel = {
										..._.omit(pcnlItem, ['riskAssessment']),
										riskAssessment: items,
									};
									onUpdatePCNLItem(newPCNLItems);
								}}
							/>
						</AccordionItem>
					</Conditional>

					<AccordionItem isActive title="Anomaly Locations">
						<AnomalyLocations
							initialAnomalyLocations={pcnlItem.anomalyLocations}
							onAdd={(images: PDFImageProps[]) => {
								const newPCNLItems: PCNLItemModel = { ..._.omit(pcnlItem, ['anomalyLocations']), anomalyLocations: images };
								onUpdatePCNLItem(newPCNLItems);
							}}
						/>
					</AccordionItem>

					<AccordionItem isActive title="General Photos">
						<VIPhotoRecordForm
							initialGeneralPhotos={pcnlItem.generalPhotos}
							onProceed={(images: PDFImageProps[]) => {
								const newPCNLItems: PCNLItemModel = {
									..._.omit(pcnlItem, ['generalPhotos']),
									generalPhotos: images,
								};

								onUpdatePCNLItem(newPCNLItems);
							}}
						/>
					</AccordionItem>

					<Conditional isVisible={matchPhrases(pcnlItem.hasLPTData, 'yes')}>
						<AccordionItem isActive title="LPT Data">
							<LPTReportForm
								initialLPTData={pcnlItem.lptData}
								photoNumbers={mappedPhotoNumbers}
								onAdd={(lptData: CustomLPTReport) => {
									const newPCNLItems: PCNLItemModel = { ..._.omit(pcnlItem, ['lptData']), lptData };
									onUpdatePCNLItem(newPCNLItems);
								}}
							/>
						</AccordionItem>
					</Conditional>

					<Conditional isVisible={matchPhrases(pcnlItem.hasMPTData, 'yes')}>
						<AccordionItem isActive title="MPT Data">
							<MPIReportForm
								initialMPTData={pcnlItem.mptData}
								photoNumbers={mappedPhotoNumbers}
								onAdd={(mptData: CustomMPIReport) => {
									const newPCNLItems: PCNLItemModel = {
										..._.omit(pcnlItem, ['mptData']),
										mptData,
									};
									onUpdatePCNLItem(newPCNLItems);
								}}
							/>
						</AccordionItem>
					</Conditional>

					<Conditional isVisible={matchPhrases(pcnlItem.hasUTMData, 'yes')}>
						<AccordionItem isActive title="UTM Data">
							<AccordionItem isActive title="UTM Overview Data">
								<CustomUTMInspectionOverviewForm
									initialData={pcnlItem.utmData.overview}
									onProceed={(
										overview: PCNLItemModel['utmData']['overview']
									) => {
										const newUTMData = {
											overview,
											items: pcnlItem.utmData.items,
										};

										const newPCNLItem: PCNLItemModel = {
											..._.omit(pcnlItem, ['utmData']),
											utmData: newUTMData,
										};
										onUpdatePCNLItem(newPCNLItem);
									}}
								/>
							</AccordionItem>

							<AccordionItem isActive title="UTM Inspection Items">
								<CustomNewItem label="Add New UTM Item">
									<CustomUTMInspectionFormNewItem
										onAdd={handleAddInspectionItem}
									/>
								</CustomNewItem>

								{pcnlItem.utmData.items.map((item, index) => (
									<CustomUTMInspectionItem
										onDeleteInspectionItem={handleDeleteInspectionItem}
										isOpenOnDefault={index === pcnlItem.utmData.items.length - 1}
										key={item.sectionTitle}
										includeSize={false}
										options={getUTMStructureInspectionOptions()}
										corrosionAllowance={pcnlItem.utmData.overview.corrosionAllowance}
										initialData={item}
										onEditInspectionItem={(updatedItem) => handleEditInspectionItem(item, updatedItem)}
									/>
								))}
							</AccordionItem>
						</AccordionItem>
					</Conditional>

					<Conditional isVisible={matchPhrases(pcnlItem.hasConcreteData, 'yes')}>
						<AccordionItem isActive title="Concrete Hammer Test Report">
							<ConcreteHammerTemplate
								item={pcnlItem.concreteHammerData} 
								onDataChange={(concreteHammerData) => onUpdatePCNLItem({ ...pcnlItem, concreteHammerData })}
							/>
						</AccordionItem>
					</Conditional>
				</CustomGroupItem>
			</Conditional>
		</>
	);
};

export default PCNLItemForm;
